import { useDatePicker } from '@/components/MyDatePicker'
import dayjs from 'dayjs'
import {
  useReadableCost,
  useReadableEr,
  useReadableRevenue,
} from '@/hooks/auth'
import { useMemo } from 'react'
import useSWR from 'swr'
import { getDateRange } from '@/lib/date-utils'
import { getDrBaseline, getGlobalLatestStartDate } from '@/lib/dr-utils'

import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { Button } from '@/components/ui/button'

import {
  costColumn,
  costsDetailColumn,
  cpiColumn,
  cpnColumn,
  dauColumn,
  dnuColumn,
  erColumn,
  installsColumn,
  ltvColumn,
  originalColumn,
  profitColumn,
  revenueColumn,
  revenueDetailColumn,
  rpaColumn,
  rpnColumn,
} from './columns'

import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import Loader from '@/components/zthd/Loader'
import {
  useColumnFilters,
  VSWeekSelector,
} from '@/components/data-table-selectors'
import { usePidJP } from '@/components/DropdownPid'
import { mutateRequest } from '@/hooks'
import { usePackageName } from '@/components/DropdownPackage'
import RoiChart from './roi-chart'
import columnDate from '@/components/roi/column-date'
import { useReadableRoas } from '@/hooks/auth'
import { day0RoasColumn } from '../Home/columns'

const useColumnVisibilityStore = createColumnVisibilityStore({}, 'roi')

const DataTable = () => {
  const pids = usePidJP((state) => state.pids)
  const pid = pids.join(',') === '' ? 'totalJP' : pids.join(',')
  const country = 'JP'
  const packageName = usePackageName((state) => state.value)

  const readableRevenue = useReadableRevenue(pid, country)
  const readableCost = useReadableCost(pid, country)
  const readableEr = useReadableEr(pid, country)
  const readableRoas = useReadableRoas(pid, country)
  const { data: baselins } = useSWR(['/ers/baselines'])
  const erBaseLine = baselins?.[country]?.[pid] ?? 0.35
  const { data: drBaselines } = useSWR(['/drs/baselines'])
  // 使用 getDrBaseline 替代直接访问，支持 packageName
  const drBaseLine = useMemo(() => {
    if (!drBaselines || pids.length !== 1) return 0.5
    const pid = pids[0]
    // JPGroup 固定为 pid=14，需要传入 packageName
    const { value } = getDrBaseline(drBaselines, 0, country, pid, packageName)
    return value
  }, [drBaselines, country, pids, packageName])

  // 获取全局最新的 baseline 配置日期
  const latestBaselineDate = useMemo(() => {
    if (!drBaselines) return ''
    const day0Date = getGlobalLatestStartDate(drBaselines, 0)
    const day3Date = getGlobalLatestStartDate(drBaselines, 3)
    return day0Date || day3Date || ''
  }, [drBaselines])

  const startDate = useDatePicker((state) => state.startDate)
  const endDate = useDatePicker((state) => state.endDate)

  const queryOpt = useMemo(
    () => ({ startDate, endDate, pid: pids, country, packageName }),
    [startDate, endDate, pids, country, packageName],
  )

  const { data: revenues, isValidating: isFetchRevenueing } = useSWR([
    '/revenues',
    queryOpt,
  ])

  const { data: costs, isValidating: isFetchCosting } = useSWR([
    '/costs',
    queryOpt,
  ])

  const { data: dnus, isValidating: isFetchDnuing } = useSWR([
    '/userActives',
    queryOpt,
  ])

  const { data: ers, isValidating: isFetchErsing } = useSWR([
    readableEr ? '/ers' : null,
    queryOpt,
  ])

  const { data: day0Roas, isValidating: isFetchDay0Roas } = useSWR([
    readableRevenue && readableCost ? '/drs' : null,
    { ...queryOpt, day_n: 0 },
    queryOpt,
  ])

  const handleRefrech = () => {
    mutateRequest('/revenues')
    mutateRequest('/costs')
    mutateRequest('/userActives')
    mutateRequest('/ers')
    mutateRequest('/notes')
    mutateRequest('/drs')
  }
  const columnVisibility = useColumnVisibilityStore(
    (state) => state.columnVisibility,
  )
  const setColumnVisibility = useColumnVisibilityStore(
    (state) => state.setColumnVisibility,
  )

  const columnFilters = useColumnFilters((state) => state.columnFilters)

  const data = useMemo(() => {
    const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))
    return dateRange.map((date) => {
      const _revenues = revenues?.[date]
      const revenue = _revenues?.total
      const _costs = costs?.[date]
      const cost = _costs?.total?.costs
      const installs = _costs?.total?.installs
      const ua = dnus?.[date]
      return {
        date,
        revenues: _revenues,
        revenue,
        costs: _costs,
        cost,
        profit: revenue - cost,
        installs,
        dnu: ua?.dnu,
        dau: ua?.dau,
        original: ua?.dnu - installs,
        er: ers?.[date],
        day0Roas: day0Roas?.[date],
      }
    })
  }, [startDate, endDate, revenues, costs, dnus, ers, day0Roas])

  const columns = useMemo(() => {
    const rows = [columnDate(pid), dnuColumn, dauColumn]

    if (readableCost) {
      rows.push(originalColumn)
      rows.push(installsColumn)
    }

    if (readableRevenue) {
      rows.push(revenueColumn)
    }
    if (readableCost) {
      rows.push(costColumn)
    }

    if (readableRevenue && readableCost) {
      rows.push(profitColumn)
    }

    if (readableEr) {
      rows.push(erColumn(erBaseLine))
    }
    if (readableRoas) {
      rows.push(day0RoasColumn(drBaseLine))
    }
    if (readableCost) {
      rows.push(cpnColumn)
      rows.push(cpiColumn)
    }

    if (readableRevenue) {
      rows.push(rpnColumn)
      rows.push(rpaColumn)
    }

    if (readableRevenue && readableCost) {
      rows.push(ltvColumn)
    }

    if (readableCost) {
      rows.push({ id: 'space-end', isPlaceholder: true, enableHiding: false })
      rows.push(costsDetailColumn)
    }
    if (readableRevenue) {
      rows.push({ id: 'space-end2', isPlaceholder: true, enableHiding: false })
      rows.push(revenueDetailColumn)
    }
    return rows
  }, [
    erBaseLine,
    drBaseLine,
    pid,
    readableRevenue,
    readableCost,
    readableEr,
    readableRoas,
  ])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  return (
    <>
      <div className="flex items-center gap-4 py-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1">
        <VSWeekSelector />
        <Button onClick={handleRefrech} variant="outline">
          refresh
        </Button>
        <ChartCodeViewer>
          <RoiChart data={data} />
        </ChartCodeViewer>
        {(isFetchRevenueing ||
          isFetchCosting ||
          isFetchDnuing ||
          isFetchDay0Roas ||
          isFetchErsing) && <Loader>Loading...</Loader>}
        <div className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          er&roas base line 值只在具体的产品下是准确的;
          多个pid,或者全选时,等于35%&50%.
          Roas取的是total{latestBaselineDate && `(${latestBaselineDate})`}.
        </div>
        <DataTableViewOptions table={table} />
      </div>
      {/* <>{JSON.stringify(data[0])}</> */}
      <DataTableWithBorder table={table} />
    </>
  )
}

export default DataTable
